import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBell, FaSpinner, FaCheckDouble, FaTrash, FaEye, 
  FaExclamationCircle, FaInfinity 
} from 'react-icons/fa';
import NotificationItem from './NotificationItem';
import useNotifications from '../../hooks/useNotifications';
import { useToast } from '../ToastContainer';
import './NotificationDropdown.css';

const NotificationDropdown = ({ isOpen, onToggle, onClose }) => {
  const dropdownRef = useRef(null);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    refresh,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications();

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
      showSuccess('Notification marquée comme lue');
    } catch (error) {
      showError('Erreur lors du marquage comme lu');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      showSuccess('Notification supprimée');
    } catch (error) {
      showError('Erreur lors de la suppression');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      showSuccess('Toutes les notifications ont été marquées comme lues');
    } catch (error) {
      showError('Erreur lors du marquage');
    }
  };

  const handleRefresh = () => {
    refresh();
    showSuccess('Notifications actualisées');
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'unread') return !notif.is_read;
    if (filter === 'read') return notif.is_read;
    return true;
  });

  const getFilterCount = (filterType) => {
    if (filterType === 'unread') return unreadCount;
    if (filterType === 'read') return notifications.length - unreadCount;
    return notifications.length;
  };

  if (!isOpen) return null;

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      {/* En-tête */}
      <div className="notification-header">
        <div className="header-title">
          <FaBell className="header-icon" />
          <h3>Notifications</h3>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount}</span>
          )}
        </div>
        
        <div className="header-actions">
          <button
            className="action-btn refresh-btn"
            onClick={handleRefresh}
            title="Actualiser"
            disabled={loading}
          >
            <FaSpinner className={loading ? 'spinning' : ''} />
          </button>
          
          {unreadCount > 0 && (
            <button
              className="action-btn mark-all-btn"
              onClick={handleMarkAllAsRead}
              title="Tout marquer comme lu"
            >
              <FaCheckDouble />
            </button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="notification-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Toutes ({getFilterCount('all')})
        </button>
        <button
          className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Non lues ({getFilterCount('unread')})
        </button>
        <button
          className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
          onClick={() => setFilter('read')}
        >
          Lues ({getFilterCount('read')})
        </button>
      </div>

      {/* Contenu */}
      <div className="notification-content">
        {error && (
          <div className="notification-error">
            <FaExclamationCircle />
            <p>{error}</p>
            <button onClick={handleRefresh}>Réessayer</button>
          </div>
        )}

        {!error && filteredNotifications.length === 0 && !loading && (
          <div className="notification-empty">
            <FaBell className="empty-icon" />
            <p>
              {filter === 'unread' && 'Aucune notification non lue'}
              {filter === 'read' && 'Aucune notification lue'}
              {filter === 'all' && 'Aucune notification'}
            </p>
          </div>
        )}

        {filteredNotifications.length > 0 && (
          <div className="notification-list">
            {filteredNotifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
                onDelete={handleDelete}
                onClick={(notif) => {
                  // Ici on peut ajouter la navigation vers la page liée
                  console.log('Notification clicked:', notif);
                }}
              />
            ))}

            {/* Bouton Charger plus */}
            {hasMore && !loading && (
              <button
                className="load-more-btn"
                onClick={loadMore}
              >
                <FaInfinity />
                Charger plus
              </button>
            )}

            {loading && (
              <div className="loading-indicator">
                <FaSpinner className="spinning" />
                Chargement...
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pied de page */}
      <div className="notification-footer">
        <button 
          className="view-all-btn" 
          onClick={() => {
            navigate('/notifications');
            onClose();
          }}
        >
          <FaEye />
          Voir toutes les notifications
        </button>
      </div>
    </div>
  );
};

export default NotificationDropdown;